import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Dialog from '@mui/material/Dialog';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PresenceAvatar from '../../components/Chat/PresenceAvatar';
import { toast } from 'react-toastify';
import { URLS } from '../../helpers/URLS';
/* Its own styles, rather than relying on whichever page happened to import
   them — this dialog is opened from the Messages page and from Manage Teams,
   and it has to look the same either way. The .chat-setup rules are written
   at the top level of that file precisely so a portaled dialog can use them. */
import './Chat.scss';

const BASE = '/api/v1/';

/* Who employees actually reach when they pick a team.

   Job titles alone do not say who answers payroll or laptop questions — in
   real data HR, accounts and admin staff often share one broad department —
   so a team that has never been set up falls back to matching on titles and
   is marked as a guess here until an admin confirms it. */
const TeamSetupDialog = ({ open, onClose, onSaved }) => {
    const [teams, setTeams] = useState([]);
    const [directory, setDirectory] = useState([]);
    const [activeKey, setActiveKey] = useState(null);
    const [selected, setSelected] = useState({});
    const [term, setTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(BASE + URLS.chatTeams);
            const body = res.data;
            if (body && body.status === 200) {
                setTeams(body.data.teams || []);
                setDirectory(body.data.directory || []);
                const picks = {};
                (body.data.teams || []).forEach((team) => { picks[team.key] = new Set(team.memberCodes || []); });
                setSelected(picks);
                /* Kept where the admin already is. This reloads after a save
                   too, and snapping back to the first tab would take them off
                   the team they had just finished editing. */
                setActiveKey((prev) => (
                    prev && (body.data.teams || []).some((team) => team.key === prev)
                        ? prev
                        : (body.data.teams || [])[0]?.key || null
                ));
            } else {
                toast.error(body?.message || 'Could not load teams');
            }
        } catch (error) {
            toast.error('Could not load teams');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) { setTerm(''); load(); }
    }, [open, load]);

    const activeTeam = teams.find((t) => t.key === activeKey) || null;
    const picked = (activeKey && selected[activeKey]) || new Set();

    const toggle = (code) => {
        setSelected((prev) => {
            const next = new Set(prev[activeKey] || []);
            if (next.has(code)) next.delete(code); else next.add(code);
            return { ...prev, [activeKey]: next };
        });
    };

    /* Who the team already holds, as it stood when this tab was opened. The
       order below is pinned to this rather than to the live selection: sorting
       as you click would throw the row you just ticked to the other end of a
       long list, taking your place in it with you. It re-settles when the tab
       changes or after a save. */
    const membersOnOpen = useMemo(
        () => new Set(activeTeam?.memberCodes || []), [activeTeam]);

    const visible = useMemo(() => {
        const q = term.trim().toLowerCase();
        const matched = !q ? directory : directory.filter((p) => (
            String(p.employeeName || '').toLowerCase().includes(q)
            || String(p.employeeCode || '').toLowerCase().includes(q)
            || String(p.designation || '').toLowerCase().includes(q)
            || String(p.department || '').toLowerCase().includes(q)
        ));

        /* the team's own people first. Sort is stable, so everybody keeps
           their directory order within each of the two groups. */
        return [...matched].sort((a, b) =>
            Number(membersOnOpen.has(b.employeeCode))
            - Number(membersOnOpen.has(a.employeeCode)));
    }, [directory, term, membersOnOpen]);

    const save = async () => {
        if (!activeKey) return;
        setSaving(true);
        try {
            const res = await axios.post(BASE + URLS.chatSetTeamMembers(activeKey), {
                memberCodes: [...picked],
            });
            const body = res.data;
            if (body && body.status === 200) {
                toast.success(body.message || 'Team updated');
                /* Reloaded so the team stops being shown as a guess once it
                   has been confirmed. */
                await load();
                if (onSaved) onSaved();
            } else {
                toast.error(body?.message || 'Could not save the team');
            }
        } catch (error) {
            toast.error('Could not save the team');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className="chat-setup-dialog">
            <div className="chat-setup">
                <div className="chat-setup-head">
                    <div>
                        <h6>Manage chat teams</h6>
                        <p>Choose who employees reach when they pick a team.</p>
                    </div>
                    <button type="button" className="chat-setup-close" onClick={onClose}>Close</button>
                </div>

                <div className="chat-setup-tabs">
                    {teams.map((team) => (
                        <button
                            type="button"
                            key={team.key}
                            className={`chat-setup-tab${team.key === activeKey ? ' is-on' : ''}`}
                            onClick={() => setActiveKey(team.key)}
                        >
                            {team.label}
                            <span>{(selected[team.key] || new Set()).size}</span>
                        </button>
                    ))}
                </div>

                {activeTeam && activeTeam.source === 'inferred' && (
                    <p className="chat-setup-hint">
                        This list is a guess based on job titles. Save it to make it official.
                    </p>
                )}
                {activeTeam && activeTeam.source === 'configured' && activeTeam.updatedByName && (
                    <p className="chat-setup-hint is-set">
                        Set by {activeTeam.updatedByName}.
                    </p>
                )}

                <div className="chat-setup-search">
                    <SearchRoundedIcon />
                    <input
                        type="text"
                        placeholder="Search by name, code, designation or department…"
                        value={term}
                        onChange={(e) => setTerm(e.target.value)}
                    />
                </div>

                <div className="chat-setup-list">
                    {loading && <p className="chat-setup-note">Loading…</p>}
                    {!loading && !visible.length && <p className="chat-setup-note">Nobody matched.</p>}
                    {!loading && visible.map((person) => {
                        const on = picked.has(person.employeeCode);
                        return (
                            /* A real checkbox, in a label — so the whole row is
                               still the hit area, but the state lives on the
                               input rather than on a div that only looks like
                               one. The row cannot stay a <button>: an input
                               inside one is invalid, and the two would fight
                               over the click. */
                            <label
                                key={person.employeeCode}
                                className={`chat-setup-row${on ? ' is-on' : ''}`}
                            >
                                <input
                                    type="checkbox"
                                    className="chat-setup-check"
                                    checked={on}
                                    onChange={() => toggle(person.employeeCode)}
                                />
                                <PresenceAvatar person={person} online={false} size={34} showDot={false} />
                                <span className="chat-setup-who">
                                    <b>{person.employeeName}</b>
                                    <em>{[person.designation, person.department].filter(Boolean).join(' · ')}</em>
                                </span>
                            </label>
                        );
                    })}
                </div>

                <div className="chat-setup-foot">
                    <span>{picked.size} selected for {activeTeam?.label || 'this team'}</span>
                    <button type="button" className="chat-setup-save" onClick={save} disabled={saving || !activeKey}>
                        {saving ? 'Saving…' : 'Save team'}
                    </button>
                </div>
            </div>
        </Dialog>
    );
};

export default TeamSetupDialog;
